/**
 * Add Charge Modal Component
 * Modal for adding one-time charges to a lease
 */

import { useState } from 'react';
import { asCalendarDateString } from '@resida/shared';
import { type CreateManualChargeInput, type ChargeType } from '../../lib/api';

interface AddChargeModalProps {
  onSave: (data: CreateManualChargeInput) => Promise<void>;
  onCancel: () => void;
}

export function AddChargeModal({ onSave, onCancel }: AddChargeModalProps) {
  const [chargeType, setChargeType] = useState<ChargeType | 'late_fee'>('other');
  const [amount, setAmount] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [dueDate, setDueDate] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      const data: CreateManualChargeInput = {
        chargeType,
        amount: Math.round(parseFloat(amount) * 100),  // convert dollars to cents
        description,
        // dueDate is YYYY-MM-DD from the date input — send directly, do not convert via toISOString()
        ...(dueDate && { dueDate: asCalendarDateString(dueDate) }),
      };

      await onSave(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add charge');
    } finally {
      setIsSaving(false);
    }
  };

  const chargeTypeOptions: { value: ChargeType | 'late_fee'; label: string }[] = [
    { value: 'late_fee', label: 'Late Fee' },
    { value: 'pet_rent', label: 'Pet Rent' },
    { value: 'parking', label: 'Parking' },
    { value: 'utilities', label: 'Utilities' },
    { value: 'storage', label: 'Storage' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <div
      data-testid="add-charge-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-charge-title"
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4"
      onClick={onCancel}
    >
      <div
        className="bg-card rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="add-charge-title" className="text-xl font-semibold m-0">
          Add One-Time Charge
        </h3>
        <p className="text-sm text-muted-foreground mt-1 m-0">
          Add a one-time charge to this lease
        </p>

        {error && (
          <div
            data-testid="add-charge-error"
            role="alert"
            aria-live="polite"
            className="bg-destructive/5 border border-destructive/20 rounded-lg p-3 mt-4"
          >
            <p className="text-sm text-destructive m-0">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} data-testid="add-charge-form" className="mt-4">
          <div className="grid gap-4">
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="charge-type"
                className="text-sm font-medium text-foreground"
              >
                Charge Type *
              </label>
              <select
                id="charge-type"
                data-testid="add-charge-type-input"
                value={chargeType}
                onChange={(e) => setChargeType(e.target.value as ChargeType | 'late_fee')}
                required
                aria-required="true"
                className="w-full px-3 py-2 text-sm border border-input rounded-md min-h-[44px]"
              >
                {chargeTypeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="charge-amount"
                className="text-sm font-medium text-foreground"
              >
                Amount ($) *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-base">
                  $
                </span>
                <input
                  id="charge-amount"
                  data-testid="add-charge-amount-input"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                  aria-required="true"
                  className="w-full px-3 py-2 pl-7 text-sm border border-input rounded-md min-h-[44px]"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="charge-description"
                className="text-sm font-medium text-foreground"
              >
                Description *
              </label>
              <input
                id="charge-description"
                data-testid="add-charge-description-input"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                aria-required="true"
                placeholder="e.g., Pet policy violation fee"
                className="w-full px-3 py-2 text-sm border border-input rounded-md min-h-[44px]"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="charge-due-date"
                className="text-sm font-medium text-foreground"
              >
                Due Date (Optional)
              </label>
              <input
                id="charge-due-date"
                data-testid="add-charge-due-date-input"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-input rounded-md min-h-[44px]"
              />
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              type="button"
              data-testid="add-charge-cancel"
              onClick={onCancel}
              disabled={isSaving}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-foreground bg-card border border-border rounded-md hover:bg-accent disabled:opacity-50 min-h-[44px]"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="add-charge-submit"
              disabled={isSaving}
              aria-disabled={isSaving}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
            >
              {isSaving ? 'Adding...' : 'Add Charge'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
